import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { useUserContext } from '@hooks/context/useUserContext';
import FormField from '@components/form_field';
import Input from '@components/common/input';
import { useLocales } from '@hooks/helpers/useLocales';
import { useFrequency } from '@hooks/helpers/useFrequency';
import { EFundsOperation } from '@enum';
import Textarea from '@components/common/textarea';
import Button from '@components/common/button';
import { useFormContext } from 'react-hook-form';
import { EContactType, TContact, TTransactionMethodTypeAction } from '@xeppt/xeppt-sdk/types';
import InfoBlock from '@components/common/info_block';
import Switch from '@components/common/switch';
import { useSendAndRequestModals } from '@hooks/modals/useSendAndRequestModals';
import { Icon } from '@components/icons';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import { paymentsConfig } from '@const/payment';

import classNames from 'classnames/bind';
import { capitalizeFirstLetter } from '@utils/index';

interface IProps {
    onSubmit: () => void;
    onPrev: () => void;
    method: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>;
    contactList?: TContact[];
    chosenContact?: TContact;
    isEdit: boolean;
    isCustomContact?: boolean;
    refetchFeeTrigger?: number;
}

const cx = classNames.bind(styles);

const Amount: FC<IProps> = ({
    onSubmit,
    onPrev,
    method,
    isCustomContact,
    contactList,
    isEdit,
    chosenContact,
    refetchFeeTrigger
}) => {
    const { account, limits } = useUserContext();
    const { validationLocale, labelLocale, submitLocale, placeholderLocale } = useLocales();
    const { getValues, watch, setValue, formState, ...form } = useFormContext();
    const { amountValidation, amountResultValidation, giftCardFeePoints } = useFrequency({
        isFrequencyExist: true,
        method
    });
    const [isFirstRender, setIsFirstRender] = useState(true);
    const { handleOpenEstimatedFeesModal } = useSendAndRequestModals();

    const isBank = useMemo(
        () =>
            method === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
            method === EFundsOperation.EFT_DEBIT_DEPOSIT,
        [method]
    );

    const isWallet = useMemo(
        () =>
            method === EFundsOperation.WALLET_CREDIT_TRANSFER ||
            method === EFundsOperation.WALLET_DEBIT_REQUEST,
        [method]
    );

    const isETransfer = useMemo(
        () =>
            method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
            method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        [method]
    );

    const isMoneyRequest = useMemo(
        () =>
            method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST ||
            // Wallet request uses the gross "amount charged to payer" model only for a known
            // XEPPT (internal) payer → FundsRequestWallet. External or not-yet-resolved/custom
            // contacts route to the net-keyed payment-intent backend, so keep the old model.
            (method === EFundsOperation.WALLET_DEBIT_REQUEST &&
                chosenContact?.type === EContactType.INTERNAL),
        [method, chosenContact]
    );

    const isRequest = useMemo(
        () =>
            method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST ||
            method === EFundsOperation.EPS_DEBIT_DEPOSIT ||
            method === EFundsOperation.EFT_DEBIT_DEPOSIT ||
            method === EFundsOperation.WALLET_DEBIT_REQUEST,
        [method]
    );

    const isEps = useMemo(() => method === EFundsOperation.EPS_DEBIT_DEPOSIT, [method]);

    const isPayBills = useMemo(() => method === EFundsOperation.PAY_BILL_CREDIT_PAYMENT, [method]);

    const recipientName = useMemo(() => {
        const values = getValues();
        const foundContact = contactList?.find((item) => item.id === values.contact);
        if (isBank) return 'Recipient';
        if (isWallet || isETransfer)
            return `${foundContact?.first_name || values.new_contact?.first_name} ${foundContact?.last_name || values.new_contact?.last_name}`;
        if (isPayBills) return 'Payee';
    }, [watch()]);

    const amountLabels = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    send: 'Amount you send',
                    receive: `${recipientName} receive`
                };
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return isMoneyRequest
                    ? { send: 'Amount charged to payer', receive: 'You request' }
                    : { send: 'Amount you request', receive: 'You receive' };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL: {
                return {
                    send: 'Amount you send',
                    receive: `Deposited`
                };
            }
            case EFundsOperation.EFT_DEBIT_DEPOSIT: {
                return {
                    send: 'Amount you request',
                    receive: 'Amount you receive'
                };
            }
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER: {
                return {
                    send: 'Amount you send',
                    receive: `${recipientName} receive`
                };
            }
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST: {
                return {
                    send: 'Amount charged to payer',
                    receive: 'You request'
                };
            }
            case EFundsOperation.EPS_DEBIT_DEPOSIT: {
                return {
                    send: 'Amount you request',
                    receive: 'Amount you receive'
                };
            }
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT: {
                return {
                    send: 'Amount you send',
                    receive: `${recipientName} receive`
                };
            }
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD: {
                return {
                    send: undefined,
                    receive: 'Gift card value'
                };
            }
        }
    }, [method, recipientName, isMoneyRequest]);

    // from_charged/from_request are new backend params not yet in the SDK type — cast to pass them.
    type TFeePayloadExt = Parameters<typeof apiAccountService.calculateLoyaltyFee>[0] & {
        from_charged?: boolean;
        from_request?: boolean;
    };

    const { data: pointsFeeData, handleRequest: handleGetSummary } = useApiQuery({
        method: (data?: {
            amount: number;
            fee_included?: boolean;
            from_charged?: boolean;
            from_request?: boolean;
        }) => {
            const amount = data?.amount ?? Number(getValues().amount) ?? 0;
            const base = { ...paymentsConfig[method], amount };
            if (data?.from_charged) {
                return apiAccountService.calculateLoyaltyFee({
                    ...base,
                    fee_included: false,
                    from_charged: true,
                } as TFeePayloadExt);
            }
            if (data?.from_request) {
                return apiAccountService.calculateLoyaltyFee({
                    ...base,
                    fee_included: false,
                    from_request: true,
                } as TFeePayloadExt);
            }
            return apiAccountService.calculateLoyaltyFee({
                ...base,
                fee_included: data?.fee_included ?? false,
            });
        },
        isInitialRequest: false,
        deps: [method, refetchFeeTrigger],
        condition:
            !!method &&
            method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD &&
            (getValues().amount > 0 || getValues().amount_result > 0),
        onSuccess: (data) => {
            if (data.amount >= 0 && data.total >= 0) {
                setValue('amount', data.amount.toFixed(2));
                setValue('amount_result', data.total.toFixed(2));
                form.trigger(['amount', 'amount_result']);
            }
        }
    });

    const pointsFeeResult = useMemo(() => {
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return giftCardFeePoints;
        } else {
            return pointsFeeData;
        }
    }, [pointsFeeData, giftCardFeePoints]);

    useEffect(() => {
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            if ((pointsFeeResult?.fee || 0) > (account?.balance || 0)) {
                form.setError('amount', { message: validationLocale('not_enough_balance') });
            } else {
                form.clearErrors('amount');
            }
        }
    }, [method, pointsFeeResult]);

    useEffect(() => {
        if (isFirstRender && Number(getValues().amount) > 0) {
            setIsFirstRender(false);
        }
    }, [watch(), isFirstRender]);

    // Scheduling is supported only for wallet requests to a XEPPT (internal) payer — that path
    // reuses the gross-keyed FundsRequestWallet executor on each run. e-Transfer requests and
    // external/custom wallet-request payers have no scheduled executor path and keep the switch
    // hidden, so clear any stale isSchedule (e.g. toggled on, then the payer/method changed)
    // before submit can route a non-schedulable request to createSchedulePayment.
    useEffect(() => {
        const isWalletInternalRequest =
            method === EFundsOperation.WALLET_DEBIT_REQUEST &&
            chosenContact?.type === EContactType.INTERNAL;
        const isUnschedulableRequest =
            (method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST ||
                method === EFundsOperation.WALLET_DEBIT_REQUEST) &&
            !isWalletInternalRequest;
        if (isUnschedulableRequest && getValues().isSchedule) {
            setValue('isSchedule', false);
        }
    }, [method, chosenContact]);

    const currentLimits = useMemo(() => {
        return limits?.[method as TTransactionMethodTypeAction];
    }, [method, limits]);

    const limitsLength = useMemo(() => {
        let counter = 0;
        if (currentLimits?.per_transfer !== 0) counter++;
        if (currentLimits?.daily !== 0) counter++;
        if (currentLimits?.weekly !== 0) counter++;
        if (currentLimits?.monthly !== 0) counter++;
        return counter;
    }, [currentLimits]);

    const isLimitsReached = useMemo(() => {
        const m = method as TTransactionMethodTypeAction;
        if (
            limits?.[m]?.daily !== 0 &&
            (limits?.[m]?.daily || 0) === (limits?.[m]?.daily_used || 0)
        ) {
            return 'daily';
        }
        if (
            limits?.[m]?.weekly !== 0 &&
            (limits?.[m]?.weekly || 0) === (limits?.[m]?.weekly_used || 0)
        ) {
            return 'weekly';
        }
        if (
            limits?.[m]?.monthly !== 0 &&
            (limits?.[m]?.monthly || 0) === (limits?.[m]?.monthly_used || 0)
        ) {
            return 'monthly';
        }
        return undefined;
    }, [limits]);

    const isLimitsExists = useMemo(() => {
        return (
            currentLimits?.per_transfer !== 0 ||
            currentLimits?.amount_transfer_min !== 0 ||
            currentLimits?.daily !== 0 ||
            currentLimits?.weekly !== 0 ||
            currentLimits?.monthly !== 0
        );
    }, []);

    const methodText = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'send';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'request';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'withdraw';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'load';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'send';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'request';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'load';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'payment';
        }
    }, [method]);

    return (
        <div className={styles.wrapper}>
            {isLimitsReached && (
                <InfoBlock variant="warning">
                    <Typography variant="body4" weight="bold">
                        {capitalizeFirstLetter(isLimitsReached)} sending limit reached
                    </Typography>
                    <Typography variant="body4">
                        You have reached your {isLimitsReached} limit for this operation. Please try
                        again later.
                    </Typography>
                </InfoBlock>
            )}
            <div className={styles.header}>
                <div className={styles.title}>
                    {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD
                        ? 'Gift card details'
                        : 'Amount'}
                </div>
                <div className={styles.description}>
                    {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD
                        ? 'Enter the gift card details and choose whether to create one or multiple cards'
                        : `Enter the amount you want to ${methodText}`}
                </div>
            </div>
            <div className={styles.form}>
                {isLimitsExists && (
                    <div className={cx([styles.limits_wrapper, { isRow: limitsLength < 3 }])}>
                        <div className={styles.title}>
                            <Typography variant="body4" weight="semibold">
                                Limits
                            </Typography>{' '}
                        </div>
                        <div className={cx([styles.limits_values, { isRow: limitsLength < 3 }])}>
                            {currentLimits?.per_transfer !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Maximum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>{(currentLimits?.per_transfer || 0)?.toFixed(2)} CAD</b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.amount_transfer_min !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Minimum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>
                                            {(currentLimits?.amount_transfer_min || 0)?.toFixed(2)}{' '}
                                            CAD
                                        </b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.daily !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Daily
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.daily || 0) -
                                                (currentLimits?.daily_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.daily || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.weekly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Weekly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.weekly || 0) -
                                                (currentLimits?.weekly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.weekly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.monthly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Monthly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.monthly || 0) -
                                                (currentLimits?.monthly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.monthly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!isRequest && (
                    <Typography variant="body3" className={styles.balance}>
                        Current XEPPT wallet balance:{' '}
                        <span>${(account?.balance || 0)?.toFixed(2)} CAD</span>
                    </Typography>
                )}
                <div className={styles.row}>
                    {method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                        <FormField
                            name="amount_result"
                            renderComponent={({ onChange, value, ...props }) => (
                                <Input
                                    full
                                    label={amountLabels?.send}
                                    prefix="CAD"
                                    rightIcon="canadian"
                                    placeholder={placeholderLocale('amount_placeholder')}
                                    fixedAmount
                                    onlyNumbers
                                    hideErrorIcon
                                    onChange={(val) => {
                                        onChange(val);
                                    }}
                                    value={value}
                                    onBlur={() => {
                                        if (
                                            !formState?.errors?.['amount_result'] &&
                                            Number(value) !== 0
                                        ) {
                                            if (isMoneyRequest) {
                                                handleGetSummary({
                                                    amount: Number(value),
                                                    from_charged: true
                                                });
                                            } else {
                                                handleGetSummary({
                                                    amount: Number(value),
                                                    fee_included: true
                                                });
                                            }
                                        }
                                    }}
                                    {...props}
                                />
                            )}
                            rules={{
                                required: { value: true, message: validationLocale('amount') },
                                validate: amountResultValidation
                            }}
                        />
                    )}
                    <FormField
                        name="amount"
                        renderComponent={({ onChange, value, ...props }) => (
                            <Input
                                full
                                label={amountLabels?.receive}
                                prefix="CAD"
                                rightIcon="canadian"
                                placeholder={placeholderLocale('amount_placeholder')}
                                fixedAmount
                                onlyNumbers
                                hideErrorIcon
                                onChange={(val) => {
                                    onChange(val);
                                }}
                                value={value}
                                onBlur={() => {
                                    if (
                                        !formState?.errors?.['amount'] &&
                                        Number(value) !== 0 &&
                                        method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD
                                    ) {
                                        if (isMoneyRequest) {
                                            handleGetSummary({
                                                amount: Number(value),
                                                from_request: true
                                            });
                                        } else {
                                            handleGetSummary({
                                                amount: Number(value),
                                                fee_included: false
                                            });
                                        }
                                    }
                                }}
                                {...props}
                            />
                        )}
                        rules={{
                            required: {
                                value: true,
                                message: validationLocale('amount')
                            },
                            validate: amountValidation
                        }}
                    />
                </div>
                {(pointsFeeResult?.fee || 0) > 0 && (
                    <div className={styles.fee_wrapper}>
                        <Typography variant="body4">
                            Includes estimated fees:{' '}
                            <b>{pointsFeeResult?.fee.toFixed(2) || '0.00'} CAD</b>
                        </Typography>
                        <Button
                            leftIcon="info"
                            variant="text-gold"
                            size="small"
                            onClick={() =>
                                handleOpenEstimatedFeesModal({
                                    amount: getValues().amount || 0,
                                    method,
                                    fee: pointsFeeResult?.fee || 0
                                })
                            }>
                            {submitLocale('get_info')}
                        </Button>
                    </div>
                )}
                {(pointsFeeResult?.points || 0) > 0 && (
                    <Typography variant="body3" className={styles.earn_points}>
                        <Icon name="coins" /> On this transaction you will earn{' '}
                        <b>{pointsFeeResult?.points.toFixed(2) || '0.00'} points</b>
                    </Typography>
                )}
                {method === EFundsOperation.WALLET_CREDIT_TRANSFER &&
                    (chosenContact?.type === EContactType.EXTERNAL || isCustomContact) && (
                        <div className={styles.expiry}>
                            Payment expires after{' '}
                            <FormField
                                name="expires_in"
                                renderComponent={(props) => <Input {...props} onlyNumbers />}
                            />{' '}
                            days
                        </div>
                    )}
                {isETransfer ||
                    (isWallet &&
                        !isCustomContact &&
                        chosenContact?.type !== EContactType.EXTERNAL) ||
                    ((chosenContact?.type === EContactType.EXTERNAL || isCustomContact) &&
                        method === EFundsOperation.WALLET_DEBIT_REQUEST) ||
                    (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                        <FormField
                            name="message"
                            renderComponent={(props) => (
                                <Textarea
                                    full
                                    label={labelLocale('message_optional')}
                                    maxLength={250}
                                    placeholder={placeholderLocale('add_message_necessary')}
                                    {...props}
                                    helperText={
                                        <>
                                            <Icon name="filled_info" width={16} height={16} />
                                            This description will be shown to the recipient with the
                                            gift card
                                        </>
                                    }
                                />
                            )}
                        />
                    ))}
                {method === EFundsOperation.WALLET_CREDIT_TRANSFER &&
                    (chosenContact?.type === EContactType.EXTERNAL || isCustomContact) && (
                        <InfoBlock variant="gray">
                            <Typography variant="body4">
                                Unclaimed funds are returned to you once the {methodText} expires.
                            </Typography>
                        </InfoBlock>
                    )}
                {method !== EFundsOperation.E_TRANSFER_CREDIT_TRANSFER &&
                    // e-Transfer requests can't be scheduled: the backend scheduler has no
                    // e-Transfer executor path (no etransfer_schedules table / dispatch case), so
                    // keep the switch hidden for them. Wallet requests to a XEPPT (internal) payer
                    // ARE schedulable — the executor reuses FundsRequestWallet, already gross-keyed
                    // on each run — so they store the GROSS and the switch is shown.
                    method !== EFundsOperation.E_TRANSFER_DEBIT_REQUEST &&
                    chosenContact?.type !== EContactType.EXTERNAL &&
                    !isCustomContact &&
                    method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                        <div
                            className={styles.schedule_switch}
                            style={{ marginTop: isBank || isEps ? 24 : 0 }}>
                            <FormField<boolean>
                                name="isSchedule"
                                renderComponent={({ value, onChange }) => (
                                    <Switch checked={value} onClick={() => onChange(!value)} />
                                )}
                            />
                            <p>Schedule at a later date or set up recurring {methodText}</p>
                        </div>
                    )}
                {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                    <div
                        className={styles.schedule_switch}
                        style={{ marginTop: isBank || isEps ? 24 : 0 }}>
                        <FormField<boolean>
                            name="isBulkGiftCards"
                            renderComponent={({ value, onChange }) => (
                                <Switch checked={value} onClick={() => onChange(!value)} />
                            )}
                        />
                        <p>Create multiple gift cards</p>
                    </div>
                )}
                {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD &&
                    getValues().isBulkGiftCards && (
                        <FormField
                            name="giftCardCount"
                            renderComponent={(props) => (
                                <Input full label="Quantity of gift cards" onlyNumbers {...props} />
                            )}
                            rules={{
                                required: {
                                    value: true,
                                    message: validationLocale('gift_cards_quantity')
                                },
                                validate: (value: string) => {
                                    if (Number(value) < 1) {
                                        return validationLocale('gift_cards_quantity');
                                    }
                                    return true;
                                }
                            }}
                        />
                    )}
            </div>
            <div className={styles.actions}>
                {!isEdit && (
                    <Button leftIcon="arrow_left" variant="outlined-primary" onClick={onPrev}>
                        {submitLocale('previous')}
                    </Button>
                )}
                <Button onClick={onSubmit} rightIcon="arrow_right" variant="primary">
                    {submitLocale('next')}
                </Button>
            </div>
        </div>
    );
};

export default Amount;
